export type CalendarDayStatus = 'available' | 'limited' | 'blocked';

export interface CalendarDayAvailability {
  date: string;
  capacity: number | null;
  committed_quantity: number;
  remaining_quantity: number;
  status: CalendarDayStatus;
}

/** NULL capacity = unlimited legacy quantity (Commercial Core). */
export const UNLIMITED_CALENDAR_CAPACITY = 2_147_483_647;

export function effectiveCalendarCapacity(capacity: number | null): number {
  return capacity ?? UNLIMITED_CALENDAR_CAPACITY;
}

/**
 * Hard invariant for customer calendar availability.
 * Capacity shortage must NEVER produce `blocked`.
 */
export function resolveCalendarDayStatus(params: {
  explicitManualBlock: boolean;
  capacity: number | null;
  committedQuantity: number;
  requestedQuantity: number;
}): CalendarDayStatus {
  if (params.explicitManualBlock) {
    return 'blocked';
  }

  const remaining =
    effectiveCalendarCapacity(params.capacity) - params.committedQuantity;

  if (remaining >= params.requestedQuantity) {
    return 'available';
  }

  return 'limited';
}

export function computeCalendarDayAvailability(params: {
  date: string;
  explicitManualBlock: boolean;
  capacity: number | null;
  committedQuantity: number;
  requestedQuantity: number;
}): CalendarDayAvailability {
  const effectiveCapacity = effectiveCalendarCapacity(params.capacity);
  const remaining = effectiveCapacity - params.committedQuantity;

  return {
    date: params.date,
    capacity: params.capacity,
    committed_quantity: params.committedQuantity,
    remaining_quantity: remaining,
    status: resolveCalendarDayStatus(params),
  };
}

export function monthHasLimitedDays(
  dayAvailability: Record<string, CalendarDayStatus>
): boolean {
  return Object.values(dayAvailability).some((status) => status === 'limited');
}

export const LIMITED_AVAILABILITY_LEGEND = 'Begränsad tillgänglighet';

export const LIMITED_AVAILABILITY_INFO_TITLE = 'Om begränsad tillgänglighet';

export const LIMITED_AVAILABILITY_INFO_TEXT =
  'Vissa datum kan ha begränsad tillgänglighet för det antal du valt. Du kan fortfarande boka eller skicka en förfrågan – vi kontrollerar då bästa lösningen för leveransen.';

export const BLOCKED_DATE_LEGEND = 'Datum spärrat';
